import { useMemo } from "react";
import type { EdgeProps, Node } from "reactflow";
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  useReactFlow,
} from "reactflow";

import { useAppSelector } from "@/reduxHooks.ts";

import { getNodeStatus } from "../../redux/workflow-slice";
import { NODE_STATUS } from "../../utils/constants";

import { MarkerColors } from "./custom-markers";
import { CustomEdgeToolbar } from "./edge-toolbar";

export function CustomEdge({
  id,
  source,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  style = {},
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    borderRadius: 20,
    sourcePosition,
    sourceX,
    sourceY,
    targetPosition,
    targetX,
    targetY,
  });
  const { getNode, deleteElements, getNodes } = useReactFlow();
  const sourceNode = getNode(source) as Node;
  const currentNodeRunStatus = useAppSelector(getNodeStatus(source));

  const nodeStatus = useMemo(() => {
    if (selected) return "Selected";
    else if (currentNodeRunStatus?.nodeStatus) {
      // if the node is running, return the active status from run_status API
      return currentNodeRunStatus.nodeStatus;
    } else {
      // use the node status from the node data
      return sourceNode.data?.nodeStatus ?? NODE_STATUS.DEFAULT;
    }
  }, [selected, currentNodeRunStatus, sourceNode.data?.nodeStatus]);

  const handleDelete = () => {
    deleteElements({ edges: [{ id }] });
  };

  const { toolbarX, toolbarY } = useMemo(() => {
    return {
      toolbarX: labelX,
      toolbarY: sourceY < targetY ? sourceY - 40 : targetY - 40,
    };
  }, [labelX, sourceY, targetY]);

  // Check if any nodes are selected
  const shouldShowToolbar = useMemo(() => {
    if (!selected) return false;
    const nodes = getNodes();
    const selectedNodes = nodes.filter(node => node.selected);
    return selectedNodes.length === 0;
  }, [selected, getNodes]);

  return (
    <>
      <BaseEdge
        id={id}
        style={{
          ...style,
          zIndex: 5,
          stroke: MarkerColors[nodeStatus],
          strokeWidth: selected ? 1.5 : 1.25,
        }}
        path={edgePath}
        markerEnd={`url(#endMarker-${nodeStatus})`}
        markerStart={`url(#startMarker-${nodeStatus})`}
      />
      {shouldShowToolbar && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${toolbarX}px,${toolbarY}px)`,
              pointerEvents: "all",
              zIndex: 3000,
            }}
          >
            <CustomEdgeToolbar id={id} onDelete={handleDelete} />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
